import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { GALLERY } from "@/data";

const LINES = ["AVIATION", "AESTHETICS", "& PRECISION"];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.12 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.62, 0.9]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src="https://images.pexels.com/photos/25724429/pexels-photo-25724429.jpeg"
          alt="Private jet on tarmac at night"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-ink" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 pt-40 md:pt-52 pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="overline">Aero Detail Lab Ltd</span>
        </motion.div>

        <h1 className="font-display font-semibold tracking-tighter leading-[0.92] text-[clamp(2.5rem,10vw,7rem)]">
          {LINES.map((line, i) => (
            <span key={line} className="reveal-mask pb-[0.08em]">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="show"
                className="block pr-[0.08em]"
              >
                {line === "& PRECISION" ? (
                  <>
                    <span className="text-white/40">&amp;</span>{" "}
                    <span className="text-purple">PRECISION</span>{" "}
                    <span className="text-lime">CARE</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-12 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-md text-white/70 text-base md:text-lg font-body">
            The laboratory, a mobile service that comes to you, for aircraft appearance.
            Clinical, aviation-grade detailing, from single-engine props to private jets.
          </p>
          <button
            data-testid="hero-book-btn"
            onClick={() => document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-fill self-start md:self-auto border border-white text-white px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] whitespace-nowrap"
          >
            Book Your Aircraft
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50"
      >
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
